/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description AddClaimAction Modal
 */
import _ from 'lodash';
import { Edit, Plus } from '@icon-park/react';
import { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Input, Switch, Button, ButtonProps, Tooltip } from 'antd';

import useUpdateClaimActivityAction from 'hooks/claim/mutate/useUpdateClaimActivityAction';
import { WORKLIST_ENTITY_TYPES } from 'app-constants/claims/workLists';
import { ACTIVITY_TYPE_POST_CATEGORIES } from 'app-constants/admin/entitySettings';
import { NO_ACCESS_TOOLTIP_TEXT } from '@veritable/common/constants';
import { useAppStoreValues } from 'store/app';
import { isDuplicateName } from 'utils/admin/entitySettings';

/**
 * initial state for add claim action
 */
const initialState: ClaimRequest.IActivityTypePost = {
	name: '',
	isDeleted: false,
	category: ACTIVITY_TYPE_POST_CATEGORIES.user,
	entityType: WORKLIST_ENTITY_TYPES.claim,
};

export interface IProps extends ButtonProps {
	activity?: ClaimRequest.IActivityType;
	iconOnly?: boolean;
	title?: string;
	existingNames?: string[];
}

/**
 * AddClaimAction
 * @returns {React.FC<IProps>} AddClaimAction
 */

const AddClaimAction: React.FC<IProps> = ({ activity, iconOnly, title, existingNames, ...restProps }) => {
	const [open, setOpen] = useState(false);
	const initialValues = useMemo(() => activity || initialState, [activity]);
	const [activityMeta, setActivityMeta] = useState<ClaimRequest.IActivityTypePost | null>(initialValues);
	const { canAddClaims } = useAppStoreValues().userAccess;
	const { mutateAsync, isPending } = useUpdateClaimActivityAction();
	const [form] = Form.useForm();

	const disableButton = restProps.disabled || !canAddClaims;
	const isDuplicate = isDuplicateName({
		initialName: activity?.name,
		currentName: activityMeta?.name,
		allNames: existingNames,
	});
	const disabled =
		isDuplicate ||
		((_.isEmpty(activityMeta?.name) || _.isEqual(activityMeta?.name, activity?.name)) &&
			(_.isEmpty(activityMeta?.name) || _.isEqual(activityMeta?.isDeleted, !!activity?.isDeleted)));

	// use effect to set the activity meta
	useEffect(() => {
		if (!_.isEmpty(activity)) {
			setActivityMeta({ ...activity, category: ACTIVITY_TYPE_POST_CATEGORIES.user });
		}
	}, [activity]);

	// handle cancel
	const handleCancel = () => {
		setOpen(false);
		setActivityMeta(_.cloneDeep(initialState));
		form?.resetFields();
	};

	// handle ok
	const handleOk = () => {
		form.validateFields().then(() => {
			const submitModel: ClaimRequest.IActivityTypePost = {
				...activityMeta,
				id: activity?.id,
				description: activity?.description || activityMeta?.name,
			};
			mutateAsync({ model: { entityType: WORKLIST_ENTITY_TYPES.claim, activityTypePost: submitModel } }, { onSuccess: handleCancel });
		});
	};

	// get button
	const getButton = () => {
		if (iconOnly) {
			return <Button icon={<Edit />} onClick={() => setOpen(true)} {...restProps} disabled={disableButton} />;
		}
		return (
			<Button onClick={() => setOpen(true)} type='link' icon={<Plus />} {...restProps} disabled={disableButton}>
				{'Add'}
			</Button>
		);
	};

	return (
		<>
			<Tooltip title={canAddClaims ? (iconOnly ? 'Edit' : '') : NO_ACCESS_TOOLTIP_TEXT}>{getButton()}</Tooltip>
			<Modal
				title={title || 'Add Activity'}
				open={open}
				onCancel={handleCancel}
				onOk={handleOk}
				okText='Save'
				cancelText='Cancel'
				cancelButtonProps={{ disabled: isPending }}
				destroyOnHidden
				okButtonProps={{ disabled, loading: isPending }}
			>
				<Form colon={false} form={form} layout='vertical' initialValues={initialValues}>
					<Form.Item
						label='Activity'
						name='name'
						rules={[
							{ required: true, message: 'Please enter an activity name' },
							{
								validator: (__, value) => {
									const duplicateName = isDuplicateName({
										initialName: activity?.name,
										currentName: value,
										allNames: existingNames,
									});
									if (duplicateName) {
										return Promise.reject('Activity already exists');
									}
									return Promise.resolve();
								},
							},
						]}
					>
						<Input
							value={activityMeta?.name}
							placeholder='Enter activity name'
							onChange={(e) => setActivityMeta({ ...activityMeta, name: e.target.value })}
						/>
					</Form.Item>

					<Form.Item label='Status' name='enabled' className='mb-0'>
						<Switch
							checked={!activityMeta?.isDeleted}
							onChange={(val) => setActivityMeta({ ...activityMeta, isDeleted: !val })}
						/>
					</Form.Item>
				</Form>
			</Modal>
		</>
	);
};

export default AddClaimAction;
